import React from 'react';
import { Drawer, List, ListItemButton, ListItemIcon, ListItemText, Toolbar, Divider, useTheme, IconButton, useMediaQuery, ListSubheader, Box, Typography } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import EventIcon from '@mui/icons-material/Event';
import SchoolIcon from '@mui/icons-material/School';
import BarChartIcon from '@mui/icons-material/BarChart';
import ClassIcon from '@mui/icons-material/Class';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import PaymentIcon from '@mui/icons-material/Payment';
import SecurityIcon from '@mui/icons-material/Security';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import GradeIcon from '@mui/icons-material/Grade';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TableChartIcon from '@mui/icons-material/TableChart';
import LayersIcon from '@mui/icons-material/Layers';
import EmailIcon from '@mui/icons-material/Email';
import QuizIcon from '@mui/icons-material/Quiz';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';


// Add types for nav items and sections

type NavItem = {
  text: string;
  icon: React.ReactElement;
  path: string;
  adminOnly?: boolean;
  superOnly?: boolean;
};

type NavSection = {
  section: string;
  items: NavItem[];
};

const drawerWidth = 240;

const SidebarNav: React.FC<{ collapsed: boolean; onToggleCollapse: () => void }> = ({ collapsed, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Grouped nav items by section
  const navSections: NavSection[] = [
    {
      section: 'General',
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', adminOnly: false, superOnly: false },
      ],
    },
    {
      section: 'Students',
      items: [
        { text: 'Students', icon: <PeopleIcon />, path: '/students', adminOnly: false, superOnly: false },
        { text: 'Enrollment Management', icon: <PeopleIcon />, path: '/enrollment-management', adminOnly: true, superOnly: false },
      ],
    },
    {
      section: 'Academics',
      items: [
        { text: 'Courses', icon: <MenuBookIcon />, path: '/courses', adminOnly: true, superOnly: false },
        { text: 'Grade Management', icon: <GradeIcon />, path: '/grades', adminOnly: true, superOnly: false },
        { text: 'Bulk Grade Entry', icon: <TableChartIcon />, path: '/bulk-grading', adminOnly: true, superOnly: false },
        { text: 'Exam Management', icon: <AssignmentIcon />, path: '/exams', adminOnly: true, superOnly: false },
        { text: 'Class Sessions', icon: <ClassIcon />, path: '/class-sessions', adminOnly: true, superOnly: false },
        { text: 'Class Teacher Assignment', icon: <AssignmentIcon />, path: '/class-teacher-assignment', adminOnly: true, superOnly: false },
      ],
    },
    {
      section: 'Attendance',
      items: [
        { text: 'Attendance', icon: <EventIcon />, path: '/attendance', adminOnly: false, superOnly: false },
      ],
    },
    {
      section: 'Cameras',
      items: [
        { text: 'Cameras', icon: <CameraAltIcon />, path: '/cameras', adminOnly: false, superOnly: false },
      ],
    },
    {
      section: 'Users',
      items: [
        { text: 'Users', icon: <SchoolIcon />, path: '/users', adminOnly: true, superOnly: false },
        { text: 'Signature Management', icon: <AssignmentIcon />, path: '/signature-management', adminOnly: true, superOnly: false },
        { text: 'My Signature', icon: <AssignmentIcon />, path: '/my-signature', adminOnly: false, superOnly: false },
      ],
    },
    {
      section: 'Reports',
      items: [
        { text: 'Reports', icon: <BarChartIcon />, path: '/reports', adminOnly: false, superOnly: false },
      ],
    },
    {
      section: 'School',
      items: [
        { text: 'School Management', icon: <SchoolIcon />, path: '/school-management', adminOnly: false, superOnly: true },
        { text: 'School Applications', icon: <AssignmentIcon />, path: '/school-applications', adminOnly: false, superOnly: true },
        { text: 'School Subscription', icon: <PaymentIcon />, path: '/school-subscription', adminOnly: true, superOnly: false },
        { text: 'School Invoices', icon: <ReceiptIcon />, path: '/school-invoices', adminOnly: true, superOnly: false },
        { text: 'School Settings', icon: <SchoolIcon />, path: '/admin-settings', adminOnly: true, superOnly: false },
      ],
    },
    {
      section: 'Finance',
      items: [
        { text: 'Subscription Management', icon: <PaymentIcon />, path: '/subscription-management', adminOnly: false, superOnly: true },
        { text: 'Invoice Management', icon: <ReceiptIcon />, path: '/invoices', adminOnly: false, superOnly: true },
      ],
    },
    {
      section: 'System',
      items: [
        { text: 'Token Management', icon: <SecurityIcon />, path: '/token-management', adminOnly: false, superOnly: true },
        { text: 'System Settings', icon: <SettingsIcon />, path: '/system-settings', adminOnly: false, superOnly: true },
        { text: 'Class Level Management', icon: <LayersIcon />, path: '/class-level-management', adminOnly: true, superOnly: false },
      ],
    },
    {
      section: 'Communications',
      items: [
        { text: 'Communications Center', icon: <EmailIcon />, path: '/communications', adminOnly: false, superOnly: true },
        { text: 'School Communications', icon: <EmailIcon />, path: '/school-communications', adminOnly: true, superOnly: false },
        { text: 'Grade Details Sender', icon: <GradeIcon />, path: '/grade-details-sender', adminOnly: true, superOnly: false },
        // Add Send Notification for superuser/admin
        { text: 'Send Notification', icon: <NotificationsIcon />, path: '/send-notification', adminOnly: true, superOnly: true },
      ],
    },
    {
      section: 'Quizzes',
      items: [
        { text: 'All Quizzes', icon: <QuizIcon />, path: '/quizzes', adminOnly: false, superOnly: false },
        { text: 'My Attempts', icon: <AssignmentIcon />, path: '/quizzes/attempts', adminOnly: false, superOnly: false },
        { text: 'Results Review', icon: <GradeIcon />, path: '/quizzes/results-review', adminOnly: true, superOnly: false },
      ],
    },
  ];

  const studentNavSections: NavSection[] = [
    {
      section: 'Student',
      items: [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/student-dashboard' },
        { text: 'My Quizzes', icon: <QuizIcon />, path: '/quizzes' },
        { text: 'My Attempts', icon: <AssignmentIcon />, path: '/quizzes/attempts' },
        { text: 'My Attendance', icon: <EventIcon />, path: '/myattendance' },
        { text: 'My Grades', icon: <GradeIcon />, path: '/mygrades' },
        { text: 'Announcements', icon: <EmailIcon />, path: '/announcements' },
      ],
    },
  ];

  // Collapsible section state with improved logic
  const [expandedSections, setExpandedSections] = React.useState<{ [key: string]: boolean }>(() => {
    // Initialize based on current route and user preferences
    const initial: { [key: string]: boolean } = {};
    const currentSections = user?.role === 'student' ? studentNavSections : navSections;
    
    currentSections.forEach((section) => {
      // Check if current route matches any item in this section
      const hasActiveRoute = section.items.some(item => 
        location.pathname.startsWith(item.path)
      );
      
      // Expand section if it contains the current route, or if it's the first section
      initial[section.section] = hasActiveRoute || currentSections.indexOf(section) === 0;
    });
    
    return initial;
  });

  // Smart expansion based on route changes
  React.useEffect(() => {
    const currentSections = user?.role === 'student' ? studentNavSections : navSections;
    
    currentSections.forEach((section) => {
      const hasActiveRoute = section.items.some(item => 
        location.pathname.startsWith(item.path)
      );
      
      if (hasActiveRoute && !expandedSections[section.section]) {
        setExpandedSections(prev => ({
          ...prev,
          [section.section]: true
        }));
      }
    });
  }, [location.pathname, user?.role]);

  const handleToggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleExpandAll = () => {
    const currentSections = user?.role === 'student' ? studentNavSections : navSections;
    const allExpanded: { [key: string]: boolean } = {};
    currentSections.forEach(section => {
      allExpanded[section.section] = true;
    });
    setExpandedSections(allExpanded);
  };

  const handleCollapseAll = () => {
    const currentSections = user?.role === 'student' ? studentNavSections : navSections;
    const allCollapsed: { [key: string]: boolean } = {};
    currentSections.forEach(section => {
      allCollapsed[section.section] = false;
    });
    setExpandedSections(allCollapsed);
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? !collapsed : true}
      onClose={isMobile ? onToggleCollapse : undefined}
      sx={{
        width: collapsed ? 64 : drawerWidth,
        flexShrink: 0,
                 [`& .MuiDrawer-paper`]: {
           width: collapsed ? 64 : drawerWidth,
           boxSizing: 'border-box',
           background: '#3498db',
           overflowX: 'hidden',
           transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
           color: theme.palette.primary.contrastText,
           borderRight: 'none',
           boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
         },
      }}
    >
      <Toolbar />
      
             {/* Header Section */}
       {!collapsed && (
         <Box sx={{ 
           p: 2, 
           textAlign: 'center',
           borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
           mb: 1
         }}>
           <Typography 
             variant="h6" 
                          sx={{ 
                fontWeight: 800,
                color: theme.palette.primary.contrastText,
                textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                letterSpacing: '0.75px',
                fontSize: '1.25rem'
              }}
           >
             CVision
           </Typography>
           <Typography 
             variant="caption" 
                          sx={{ 
                color: 'rgba(255, 255, 255, 0.85)',
                fontWeight: 600,
                letterSpacing: '0.5px',
                fontSize: '0.75rem',
                mb: 1,
                display: 'block'
              }}
           >
             School Management
           </Typography>
           
           {/* Expand/Collapse All Buttons */}
           <Box sx={{ 
             display: 'flex', 
             gap: 1, 
             justifyContent: 'center',
             mt: 1
           }}>
             <IconButton
               size="small"
               onClick={handleExpandAll}
               sx={{
                 color: 'rgba(255, 255, 255, 0.7)',
                 '&:hover': {
                   color: theme.palette.primary.contrastText,
                   backgroundColor: 'rgba(255, 255, 255, 0.1)',
                 },
                 p: 0.5
               }}
               title="Expand All"
             >
               <ExpandMore fontSize="small" />
             </IconButton>
             <IconButton
               size="small"
               onClick={handleCollapseAll}
               sx={{
                 color: 'rgba(255, 255, 255, 0.7)',
                 '&:hover': {
                   color: theme.palette.primary.contrastText,
                   backgroundColor: 'rgba(255, 255, 255, 0.1)',
                 },
                 p: 0.5
               }}
               title="Collapse All"
             >
               <ExpandLess fontSize="small" />
             </IconButton>
           </Box>
         </Box>
       )}

      <List sx={{ pt: 0, px: collapsed ? 0 : 1 }}>
        {(user?.role === 'student' ? studentNavSections : navSections).map((section, idx) => {
          const filteredItems = section.items.filter(item => {
            if (item.adminOnly && user?.role !== 'admin' && user?.role !== 'superuser') return false;
            if (item.superOnly && user?.role !== 'superuser') return false;
            return true;
          });
          
          if (filteredItems.length === 0) return null;
          const expanded = expandedSections[section.section];
          
          // Check if this section contains the current active route
          const hasActiveRoute = section.items.some(item => 
            location.pathname.startsWith(item.path)
          );
          
          return (
            <React.Fragment key={section.section}>
              <ListSubheader
                disableSticky
                onClick={() => !collapsed && handleToggleSection(section.section)}
                                                                     sx={{
                     background: hasActiveRoute ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                     color: '#ffffff',
                    fontWeight: hasActiveRoute ? 800 : 700,
                   fontSize: '0.7rem',
                   letterSpacing: '1.2px',
                   textTransform: 'uppercase',
                  pl: collapsed ? 1 : 2,
                  pr: 0,
                  py: 1.5,
                  opacity: collapsed ? 0 : 1,
                  transition: 'all 0.3s ease',
                  cursor: collapsed ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  userSelect: 'none',
                  borderLeft: hasActiveRoute ? `3px solid ${theme.palette.primary.contrastText}` : 'none',
                  '&:hover': {
                    backgroundColor: collapsed ? 'transparent' : 'rgba(255, 255, 255, 0.08)',
                    borderRadius: 1,
                  },
                  '&:active': {
                    backgroundColor: collapsed ? 'transparent' : 'rgba(255, 255, 255, 0.12)',
                  }
                }}
              >
                {!collapsed && (
                  <>
                    <span style={{ flex: 1 }}>{section.section}</span>
                    {expanded ? 
                      <ExpandLess fontSize="small" sx={{ opacity: 0.8 }} /> : 
                      <ExpandMore fontSize="small" sx={{ opacity: 0.8 }} />
                    }
                  </>
                )}
              </ListSubheader>
              
              <Collapse in={expanded && !collapsed} timeout="auto" unmountOnExit>
                {filteredItems.map((item) => {
                  const isSelected = location.pathname.startsWith(item.path);
                  return (
                    <ListItemButton
                      key={item.text}
                      selected={isSelected}
                      onClick={() => {
                        navigate(item.path);
                        if (isMobile) onToggleCollapse();
                      }}
                      sx={{
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        px: collapsed ? 1 : 2,
                        mx: collapsed ? 0.5 : 0,
                        borderRadius: 2,
                        mb: 0.5,
                        color: theme.palette.primary.contrastText,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          transform: 'translateX(4px)',
                          '&::before': {
                            opacity: 1,
                          },
                          '& .MuiListItemIcon-root': {
                            transform: 'scale(1.1)',
                          },
                          '& .MuiListItemText-primary': {
                            fontWeight: 700,
                          }
                        },
                                                 '&.Mui-selected': {
                           backgroundColor: theme.palette.primary.contrastText,
                           color: theme.palette.primary.main,
                           boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                           '&:hover': {
                             backgroundColor: theme.palette.primary.contrastText,
                             transform: 'translateX(4px)',
                           },
                          '&::before': {
                            opacity: 0,
                          }
                        },
                        '& .MuiListItemIcon-root': {
                          color: 'inherit',
                          transition: 'transform 0.3s ease',
                          minWidth: collapsed ? 0 : 40,
                          mr: collapsed ? 0 : 2,
                          justifyContent: 'center',
                        },
                        '& .MuiListItemText-primary': {
                          fontWeight: isSelected ? 700 : 600,
                          fontSize: '0.9rem',
                          letterSpacing: '0.3px',
                          fontFamily: '"Inter", "Roboto", sans-serif',
                        }
                      }}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      {!collapsed && (
                        <ListItemText 
                          primary={item.text} 
                          sx={{ 
                            fontWeight: isSelected ? 700 : 600,
                            '& .MuiTypography-root': {
                              fontSize: '0.9rem',
                              letterSpacing: '0.3px',
                              fontFamily: '"Inter", "Roboto", sans-serif',
                            }
                          }} 
                        />
                      )}
                    </ListItemButton>
                  );
                })}
              </Collapse>
              
              {idx < (user?.role === 'student' ? studentNavSections.length : navSections.length) - 1 && (
                <Divider 
                  sx={{ 
                    borderColor: 'rgba(255, 255, 255, 0.1)', 
                    my: 1.5,
                    mx: collapsed ? 1 : 2,
                    opacity: collapsed ? 0.5 : 1
                  }} 
                />
              )}
            </React.Fragment>
          );
        })}
      </List>
    </Drawer>
  );
};

export default SidebarNav; 